import { FC, useEffect, useRef, useState } from 'react';

import StepHeader from '@components/quiz/shared/StepHeader';
import CtaButton from '@components/quiz/shared/CtaButton';
import SequentialLoadingBars from '@components/quiz/ReadinessSteps/shared/SequentialLoadingBars';
import { useGytr } from '@components/quiz/GytrProvider';
import type { SanityGytrProcessingBlock } from '@lib/sanity/types';

type GytrProcessingStepProps = SanityGytrProcessingBlock & {
  onComplete: () => void;
  /**
   * Throws the squad away and returns to the import step. Offered once retrying
   * has stopped being worth recommending (FFH-2743).
   */
  onStartAgain: () => void;
  /**
   * Goes to the review pitch. Called without asking when the rating refused the
   * squad, because retrying an unrateable squad can only fail the same way and
   * an error screen in between is a button press with no other outcome
   * (FFH-2738).
   */
  onSquadRefused: () => void;
  /**
   * Returns to the import step, which opens the screenshot route. Called
   * without asking when FPL would not serve the team, because no number of
   * retries makes FPL release it sooner (FFH-2743).
   */
  onTeamUnavailable: () => void;
};

/**
 * How many attempts before retrying stops being the recommendation.
 *
 * Two: the first attempt and one retry. A third press of the same button is
 * asking the visitor to keep doing something that is not working.
 */
const REPEAT_FAILURE_THRESHOLD = 2;

/**
 * What to say about a failure, chosen by kind rather than by message.
 *
 * Every version says the squad is saved, because the question a visitor
 * actually has at this point is whether they have lost their team.
 */
const FAILURE_COPY_BY_KIND: Record<string, { title: string; body: string }> = {
  network: {
    title: "We couldn't rate your team",
    body: "The connection dropped before the rating came back. Your squad is saved, so there's nothing to re-enter."
  },
  server: {
    title: "We couldn't rate your team",
    body: "Our rating service didn't respond. Your squad is saved, and trying again usually clears it."
  },
  unknown: {
    title: "We couldn't rate your team",
    body: "Something went wrong analysing your team. Your squad is saved, so there's nothing to re-enter."
  }
};

const REPEAT_FAILURE_COPY = {
  title: 'Still not going through',
  body: "Retrying isn't getting us anywhere. Starting again from your team is the quickest way through, and it takes about a minute."
};

/**
 * Step 3: processing. Kicks the team-rating calculation on mount and runs
 * the three loading bars; advances only once the bars have finished AND the
 * rating has resolved, so the reveal always has data ready.
 */
const GytrProcessingStep: FC<GytrProcessingStepProps> = ({
  header,
  loadingSteps,
  onComplete,
  onSquadRefused,
  onStartAgain,
  onTeamUnavailable
}) => {
  const { state, actions } = useGytr();
  const [areBarsDone, setAreBarsDone] = useState(false);
  // The first attempt is the one the mount effect below fires, so a retry is 2.
  const [attemptCount, setAttemptCount] = useState(1);
  const hasTriggeredRef = useRef(false);
  const hasAdvancedRef = useRef(false);
  const hasOpenedReviewRef = useRef(false);
  const hasHandedBackRef = useRef(false);

  // Kick the rating calculation once.
  useEffect(() => {
    if (hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;
    actions.calculateRating(1);
  }, [actions]);

  // Advance only when the bars have finished and the rating is in.
  useEffect(() => {
    if (hasAdvancedRef.current) return;
    if (areBarsDone && !state.isCalculating && state.teamRating !== null) {
      hasAdvancedRef.current = true;
      onComplete();
    }
  }, [areBarsDone, state.isCalculating, state.teamRating, onComplete]);

  // A refused squad is the visitor's to fix, so hand them the pitch rather than
  // a screen whose only button leads there. Needs the read: with nothing to
  // place there is no pitch worth showing, and the message is all we have.
  const isReviewable =
    state.calculationFailure?.kind === 'squad' &&
    state.screenshotMatches !== null;

  useEffect(() => {
    if (!isReviewable || hasOpenedReviewRef.current) return;
    hasOpenedReviewRef.current = true;
    actions.setIsReviewingScreenshot(true);
    onSquadRefused();
  }, [isReviewable, actions, onSquadRefused]);

  // A team FPL will not serve is not fixable from here either. The import step
  // reads the same failure off the provider to open the screenshot route.
  const isTeamUnavailable = state.calculationFailure?.kind === 'team';

  useEffect(() => {
    if (!isTeamUnavailable || hasHandedBackRef.current) return;
    hasHandedBackRef.current = true;
    onTeamUnavailable();
  }, [isTeamUnavailable, onTeamUnavailable]);

  const failure = state.calculationFailure;
  const hasError = hasTriggeredRef.current && !state.isCalculating && failure;

  const handleRetry = () => {
    hasTriggeredRef.current = false;
    setAreBarsDone(false);
    const attempt = attemptCount + 1;
    setAttemptCount(attempt);
    actions.calculateRating(attempt);
    hasTriggeredRef.current = true;
  };

  const handleStartAgain = () => {
    actions.resetForRetry();
    onStartAgain();
  };

  // Nothing to paint while the effects above hand over to another step.
  if (isReviewable || isTeamUnavailable) return null;

  // Everything that is left is a failure editing the squad cannot fix: an
  // unreachable API, a refused backend, a dropped connection. Offering the
  // pitch here is how a dead end becomes a loop, so the answer is the failure
  // card instead (FFH-2743).
  if (hasError && failure) {
    const isRepeatFailure = attemptCount >= REPEAT_FAILURE_THRESHOLD;
    const copy = isRepeatFailure
      ? REPEAT_FAILURE_COPY
      : FAILURE_COPY_BY_KIND[failure.kind] ?? FAILURE_COPY_BY_KIND.unknown;

    return (
      <section className="flex items-start justify-center px-6 pb-16 pt-8">
        <div className="w-full max-w-sm">
          <div
            role="alert"
            data-cy="gytr-rating-failure"
            className="animate-fade-in rounded-xl border border-neutral-200 bg-white p-6"
          >
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-danger-50 font-heading text-lg font-extrabold text-danger-600"
              >
                !
              </span>
              <p className="font-heading text-lg font-extrabold leading-snug text-neutral-800">
                {copy.title}
              </p>
            </div>

            <p className="mt-3.5 font-body text-sm leading-relaxed text-neutral-600">
              {copy.body}
            </p>

            <div className="mt-5">
              {isRepeatFailure ? (
                <div className="space-y-3">
                  <CtaButton
                    onClick={handleStartAgain}
                    dataCy="gytr-rating-start-again"
                  >
                    Start again
                  </CtaButton>
                  <CtaButton
                    variant="secondary"
                    onClick={handleRetry}
                    disabled={state.isCalculating}
                    dataCy="gytr-rating-retry"
                  >
                    {state.isCalculating
                      ? 'Rating your team…'
                      : 'Try once more'}
                  </CtaButton>
                </div>
              ) : (
                <CtaButton
                  onClick={handleRetry}
                  disabled={state.isCalculating}
                  dataCy="gytr-rating-retry"
                >
                  {state.isCalculating ? 'Rating your team…' : 'Try again'}
                </CtaButton>
              )}
            </div>
          </div>

          {/* The exception itself, kept for a support conversation and kept out
              of the headline. The reference is shown only when it names a real
              Datadog session. */}
          <p
            className="mt-4 text-center font-mono text-[11px] text-neutral-400"
            data-cy="gytr-rating-failure-detail"
          >
            {failure.message}
            {failure.reference && ` · ref ${failure.reference}`}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col items-center px-6 pb-16 pt-6">
      <div className="w-full max-w-sm">
        <StepHeader
          eyebrow={header?.eyebrow}
          accentHeading={header?.heading}
          subtext={header?.subtext}
          className="mb-10"
        />
        <SequentialLoadingBars
          steps={loadingSteps ?? []}
          onComplete={() => setAreBarsDone(true)}
        />
      </div>
    </section>
  );
};

export default GytrProcessingStep;
